
import { defineComponent, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "@/store";
import { useToast } from "primevue/usetoast";
import SchedulingService from "@/services/scheduling";
import HcaptchaWidget from "@/components/HcaptchaWidget.vue";

function isEmail(email: string) {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
}

export default defineComponent({
  name: "Acompanhar",
  components: {
    HcaptchaWidget,
  },
  setup() {
    const toast = useToast();
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    store.dispatch("updateLoading", false);

    const hcaptchaSiteKey = process.env.VUE_APP_HCAPTCHA_SITE_KEY || "";
    const hcaptchaToken = ref("");
    const captchaError = ref(false);
    const captchaWidget = ref<any>(null);

    const agendamento = ref({
      codigoAgendamento: {
        value: "",
        req: true,
        erro: false,
      },
      emailProfessor: {
        value: "",
        req: true,
        erro: false,
      },
    });

    const erroForms = ref({
      active: false,
      message: "",
    });

    const validaForms = () => {
      let erroStatus = false;

      for (const input of Object.values(agendamento.value)) {
        if (input.req && (input.value === "" || input.value === null)) {
          input.erro = true;
          erroStatus = true;
        } else {
          input.erro = false;
        }
      }

      if (isEmail(agendamento.value.emailProfessor.value)) {
        agendamento.value.emailProfessor.erro = false;
      } else {
        agendamento.value.emailProfessor.erro = true;
        erroStatus = true;
      }

      if (!hcaptchaToken.value) {
        captchaError.value = true;
        erroStatus = true;
      }

      return erroStatus;
    };

    const checkInputEmail = (inputEmail: any) => {
      inputEmail.erro = !isEmail(inputEmail.value);
    };

    const changeInputEmail = (inputEmail: any) => {
      if (inputEmail.erro) {
        checkInputEmail(inputEmail);
      }
    };

    const checkInput = (input: any) => {
      input.erro = input.value === "";
    };

    const changeSchedulingCode = (event: Event) => {
      const target = event.target as HTMLInputElement;
      agendamento.value.codigoAgendamento.value = target.value
        .toUpperCase()
        .replace(/\s+/g, "");

      if (agendamento.value.codigoAgendamento.erro) {
        checkInput(agendamento.value.codigoAgendamento);
      }
    };

    const onCaptchaVerify = (token: string) => {
      hcaptchaToken.value = token;
      captchaError.value = false;
    };

    const onCaptchaExpired = () => {
      hcaptchaToken.value = "";
      captchaError.value = true;
    };

    const onCaptchaError = () => {
      hcaptchaToken.value = "";
      captchaError.value = true;
    };

    const resetCaptcha = () => {
      hcaptchaToken.value = "";
      captchaWidget.value?.reset?.();
    };

    const submitForms = async () => {
      if (validaForms()) {
        toast.add({
          severity: "error",
          summary: "Dados incompletos",
          detail: "Preencha os campos e conclua a verificação de segurança.",
          life: 5000,
        });
        return;
      }

      store.dispatch("updateLoading", true);
      let navigationStarted = false;

      try {
        const response = await SchedulingService.loginTracking({
          emailProfessor: agendamento.value.emailProfessor.value.trim(),
          codigoAgendamento:
            agendamento.value.codigoAgendamento.value.trim().toUpperCase(),
          hcaptchaToken: hcaptchaToken.value,
        });

        if (!response.data?.accessToken) {
          throw new Error("Token de acompanhamento não retornado pela API.");
        }

        SchedulingService.saveTrackingToken(response.data.accessToken);
        navigationStarted = true;
        await router.push({ name: "Status do Agendamento" });
      } catch (erro: any) {
        navigationStarted = false;
        erroForms.value.message =
          erro?.response?.data?.message || "Erro interno. Tente novamente!";
        erroForms.value.active = true;
        resetCaptcha();
      } finally {
        // Quando o login é bem-sucedido, a tela de status mantém o loader
        // ativo até terminar sua própria consulta. Isso evita o intervalo em
        // branco entre a navegação e o carregamento dos dados.
        if (!navigationStarted) {
          store.dispatch("updateLoading", false);
        }
      }
    };

    onMounted(() => {
      SchedulingService.clearTrackingToken();

      if (route.query.session === "expired") {
        erroForms.value.message =
          "Sua sessão de acompanhamento expirou. Informe os dados novamente.";
        erroForms.value.active = true;
        router.replace({ path: "/acompanhar" });
      }
    });

    return {
      submitForms,
      erroForms,
      agendamento,
      checkInputEmail,
      changeInputEmail,
      checkInput,
      changeSchedulingCode,
      hcaptchaSiteKey,
      hcaptchaToken,
      captchaError,
      captchaWidget,
      onCaptchaVerify,
      onCaptchaExpired,
      onCaptchaError,
    };
  },
});
